import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Compass, MessageSquare, ArrowRight } from "lucide-react";

const CALENDLY_URL = "https://cal.com/mindways/discovery-call";

const insights = [
  {
    icon: AlertTriangle,
    title: "The cost of skipping it",
    body: "A wrong shortlist. A year lost. A visa refusal from a documentation gap. An SOP that does not tell a coherent story. These are not abstract risks — they are the predictable outcome of moving without a structured assessment.",
    color: "#FFF7E6",
    iconColor: "#F5A000",
  },
  {
    icon: Compass,
    title: "Clarity before commitment",
    body: "Knowing which course aligns with your career goals, which country suits your budget and outcomes, and what timeline is realistic — before you begin any application — is not optional. It is the difference between a coherent journey and a series of corrections.",
    color: "#EEF0FC",
    iconColor: "#2B2F9E",
  },
  {
    icon: MessageSquare,
    title: "What good counselling looks like",
    body: "It begins with a structured conversation, not a brochure. It asks hard questions: What are your options if Plan A does not work? What does success look like in five years? It grounds the process in the student's reality, not a template.",
    color: "#F0FDF4",
    iconColor: "#10B981",
  },
];

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

export default function Counselling() {
  return (
    <section
      id="counselling"
      data-testid="counselling-section"
      style={{ padding: "100px 0", backgroundColor: "#F8FAFC" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>

        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          style={{ maxWidth: "720px", marginBottom: "60px" }}
        >
          <motion.span variants={fadeUp} style={{
            fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif",
          }}>
            Before You Decide Anything
          </motion.span>

          <motion.h2 variants={fadeUp} style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(28px, 4vw, 48px)",
            fontWeight: "700",
            color: "#0F172A",
            margin: "14px 0 20px",
            lineHeight: "1.15",
          }}>
            Counselling is not a service.
            <br />
            <span style={{ color: "#2B2F9E" }}>It is the foundation.</span>
          </motion.h2>

          <motion.p variants={fadeUp} style={{
            fontSize: "18px",
            lineHeight: "1.8",
            color: "#475569",
            fontFamily: "'DM Sans', sans-serif",
            margin: 0,
          }}>
            Most overseas education decisions are made on incomplete information — peer advice, social media impressions,
            rankings without context, and country trends that do not reflect a student's actual profile. The cost of a
            misinformed decision is significant: a mismatched course, an unsuitable country, wasted preparation time,
            and in many cases, a disrupted career.
          </motion.p>
        </motion.div>

        {/* 3 insight cards */}
        <div className="mw-grid-3" style={{ marginBottom: "48px" }}>
          {insights.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                data-testid={`counselling-insight-${i}`}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  padding: "36px 32px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.3s",
                }}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }}
              >
                <div style={{
                  width: "52px", height: "52px", borderRadius: "12px",
                  backgroundColor: item.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "20px",
                }}>
                  <Icon size={24} style={{ color: item.iconColor }} />
                </div>
                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "20px", fontWeight: "700",
                  color: "#0F172A", margin: "0 0 14px", lineHeight: "1.3",
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: "15px", lineHeight: "1.7",
                  color: "#64748B", fontFamily: "'DM Sans', sans-serif", margin: 0,
                }}>
                  {item.body}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Callout quote + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "40px",
            alignItems: "center",
            backgroundColor: "#2B2F9E",
            borderRadius: "16px",
            padding: "40px 48px",
          }}
          className="grid-cols-1 md:grid-cols-[1fr_auto]"
        >
          <div>
            <div style={{
              width: "32px", height: "3px",
              backgroundColor: "#F5A000",
              borderRadius: "2px", marginBottom: "16px",
            }} />
            <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(18px, 2.5vw, 26px)",
              fontStyle: "italic",
              color: "#fff",
              margin: 0,
              lineHeight: "1.5",
            }}>
              "The strategy session is not the beginning of our work.
              It is the beginning of your clarity."
            </p>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "14px",
              color: "rgba(255,255,255,0.6)",
              margin: "12px 0 0",
              fontWeight: "500",
            }}>
              — Anirudh Gupta, Founder, Mindways
            </p>
          </div>

          <button
            data-testid="counselling-book-call-btn"
            onClick={() => window.open(CALENDLY_URL, "_blank")}
            style={{
              backgroundColor: "#F5A000",
              color: "#fff",
              border: "none",
              padding: "16px 32px",
              borderRadius: "6px",
              fontSize: "15px",
              fontWeight: "700",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 8px 24px rgba(245,160,0,0.4)",
              transition: "all 0.2s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Book a Strategy Call <ArrowRight size={16} />
          </button>
        </motion.div>

      </div>
    </section>
  );
}
